// static/table_engine_src/totals.js
import { ensureSection } from "./util/dom.js";
import { num } from "./util/number.js";

/**
 * Installs:
 *   ctx.updateTotals()
 *
 * Expects ctx to already have (from other installs):
 *   - ctx.spec, ctx.table, ctx.cols, ctx.colByKey, ctx.hiddenCols
 *   - ctx.colIndexByKey(colKey)
 *   - ctx.getRowEls(), ctx.getInput(tr,key), ctx.getOut(tr,key)
 *   - ctx.isNumericCol(col), ctx.colScale(col), ctx.formatNumber(x, fmt)
 *   - ctx.parseNumber(x, fallback)
 *   - ctx.normalizeFormat(col), ctx.effectiveFormatForCol(col)
 *   - ctx.getAnyRawDataset(el)
 *   - ctx.autosizeColumns(keys)
 */
export function installTotals(ctx) {
  if (!ctx) return;
  const parseNum = (value, fmt = null) =>
    (typeof ctx.parseNumber === "function" ? ctx.parseNumber(value, NaN, fmt) : Number(value));

  function colFormat(col, val) {
    if (val === null || val === undefined) return "";
    if (val === "" || val === "N/A") return String(val);

    if (ctx.isNumericCol?.(col)) {
      const fmt = ctx.effectiveFormatForCol?.(col);
      const n = parseNum(val, fmt);
      if (!Number.isFinite(n)) return String(val);
      const sc = ctx.colScale?.(col) ?? 1;
      return ctx.formatNumber ? ctx.formatNumber(n * sc, fmt) : String(n * sc);
    }

    return String(val);
  }

  function readCanonFromInput(inp, col) {
    if (!inp) return NaN;

    // prefer canonical raw (unscaled)
    const raw = String(ctx.getAnyRawDataset?.(inp) ?? "").trim();
    if (raw !== "") {
      const n = parseNum(raw);
      if (Number.isFinite(n)) return n;
    }

    const fmt = ctx.effectiveFormatForCol?.(col);
    // fallback: parse DISPLAY and unscale
    const s = String(inp.value ?? "").trim();
    if (s === "") return NaN;
    const nDisp = parseNum(s, fmt);
    if (!Number.isFinite(nDisp)) return NaN;

    const sc = ctx.colScale?.(col) ?? 1;
    return nDisp / sc;
  }

  function readCanonFromOut(outEl, col) {
    if (!outEl) return NaN;

    // ✅ prefer canonical raw via helper (consistent with inputs + future-proof)
    const raw = String(ctx.getAnyRawDataset?.(outEl) ?? "").trim();
    if (raw !== "") {
      const n = parseNum(raw);
      if (Number.isFinite(n)) return n;
    }

    const fmt = ctx.effectiveFormatForCol?.(col);
    const s = String(outEl.textContent ?? "").trim();
    if (s === "" || s === "N/A") return NaN;

    const nDisp = parseNum(s, fmt);
    if (!Number.isFinite(nDisp)) return NaN;

    const sc = ctx.colScale?.(col) ?? 1;
    return nDisp / sc;
  }

  function normalizeTotalsSpec(specTotals) {
    return (specTotals || [])
      .map((t) => {
        if (!t || typeof t !== "object") return null;

        // new-ish shape
        if (t.agg || t.targetId || t.format) {
          return {
            key: t.key,
            kind: t.agg || "wavg",
            wKey: t.w || t.weight || "amount",
            elId: t.targetId || t.elId || null,
            abs: t.abs === true,
            absW: t.absW === true || t.weightMode === "abs",
            absV: t.absV === true,
          };
        }

        // legacy-ish shape
        return {
          key: t.key,
          kind: t.kind || "wavg",
          wKey: t.wKey || t.w || "amount",
          elId: t.elId || null,
          abs: t.abs === true,
          absW: t.absW === true || t.weightMode === "abs",
          absV: t.absV === true,
        };
      })
      .filter((t) => t && t.key);
  }

  const totalsDirty = {
    all: true,
    rows: new Set(),
  };

  const totalsCache = {
    sig: "",
    rowCount: -1,
    firstRow: null,
    lastRow: null,
    acc: null,
    rowContribs: null,
  };

  function markTotalsDirtyAll() {
    totalsDirty.all = true;
    totalsDirty.rows.clear();
  }

  function markTotalsDirtyRow(rowIdx) {
    if (totalsDirty.all) return;
    const r = Number(rowIdx);
    if (!Number.isInteger(r) || r < 0) {
      markTotalsDirtyAll();
      return;
    }
    totalsDirty.rows.add(r);
  }

  function markTotalsDirtyRows(rowIdxs) {
    if (!Array.isArray(rowIdxs) || !rowIdxs.length) return;
    if (totalsDirty.all) return;

    for (const it of rowIdxs) {
      const r = Number(it);
      if (!Number.isInteger(r) || r < 0) {
        markTotalsDirtyAll();
        return;
      }
      totalsDirty.rows.add(r);
    }
  }

  function structuralTotalsSpec() {
    const specTotals = ctx?.spec?.totals;
    if (!Array.isArray(specTotals) || !specTotals.length) return [];
    return normalizeTotalsSpec(specTotals);
  }

  function refreshCacheBounds(rows) {
    const arr = Array.isArray(rows) ? rows : [];
    totalsCache.rowCount = arr.length;
    totalsCache.firstRow = arr.length ? arr[0] : null;
    totalsCache.lastRow = arr.length ? arr[arr.length - 1] : null;
  }

  function canPatchStructuralCache(totalsSpec) {
    if (!Array.isArray(totalsSpec) || !totalsSpec.length) return false;
    if (totalsDirty.all || totalsDirty.rows.size) return false;
    if (!totalsCache.acc || !totalsCache.rowContribs) return false;
    return totalsCache.sig === totalsSpecSig(totalsSpec);
  }

  function markTotalsRowsInserted(rowEls = []) {
    if (!Array.isArray(rowEls) || !rowEls.length) return;
    const totalsSpec = structuralTotalsSpec();
    if (!totalsSpec.length) return;
    if (!canPatchStructuralCache(totalsSpec)) {
      markTotalsDirtyAll();
      return;
    }

    const rows = rowEls.filter((tr) => tr && tr.dataset?.teVirtualSpacer !== "1");
    if (!rows.length) return;

    for (const tr of rows) {
      const prev = totalsCache.rowContribs.get(tr);
      if (prev) applyContrib(totalsCache.acc, prev, -1);

      const next = computeRowContrib(tr, totalsSpec, ctx.colByKey);
      totalsCache.rowContribs.set(tr, next);
      applyContrib(totalsCache.acc, next, +1);
    }

    const allRows = ctx.getRowEls?.() || [];
    refreshCacheBounds(allRows);
    totalsDirty.rows.clear();
    totalsDirty.all = false;
  }

  function markTotalsRowsRemoved(rowEls = []) {
    if (!Array.isArray(rowEls) || !rowEls.length) return;
    const totalsSpec = structuralTotalsSpec();
    if (!totalsSpec.length) return;
    if (!canPatchStructuralCache(totalsSpec)) {
      markTotalsDirtyAll();
      return;
    }

    const rows = rowEls.filter((tr) => tr && tr.dataset?.teVirtualSpacer !== "1");
    if (!rows.length) return;

    for (const tr of rows) {
      const prev = totalsCache.rowContribs.get(tr);
      if (!prev) {
        markTotalsDirtyAll();
        return;
      }
      applyContrib(totalsCache.acc, prev, -1);
    }

    const allRows = ctx.getRowEls?.() || [];
    refreshCacheBounds(allRows);
    totalsDirty.rows.clear();
    totalsDirty.all = false;
  }

  function totalsSpecSig(totalsSpec) {
    return totalsSpec
      .map((t) =>
        [
          t.key || "",
          t.kind || "",
          t.wKey || "",
          t.elId || "",
          t.abs ? "1" : "0",
          t.absW ? "1" : "0",
          t.absV ? "1" : "0",
        ].join("|"),
      )
      .join(";");
  }

  function makeAcc(totalsSpec) {
    const acc = {};
    totalsSpec.forEach((t) => {
      acc[t.key] = { sum: 0, w: 0, s: 0, seen: 0 };
    });
    return acc;
  }

  function readSumValue(tr, key, colByKey) {
    const col = colByKey?.get?.(key);
    const inp = ctx.getInput?.(tr, key);
    if (inp) {
      return (col && col.type === "number") ? readCanonFromInput(inp, col) : num(inp.value, NaN);
    }

    const outEl = ctx.getOut?.(tr, key);
    if (outEl && col && ctx.isNumericCol?.(col)) return readCanonFromOut(outEl, col);
    if (outEl) return num(outEl.textContent, NaN);
    return NaN;
  }

  function readWavgParts(tr, t, colByKey) {
    const key = t.key;
    const wKey = t.wKey || "amount";

    const wCol = colByKey?.get?.(wKey);
    const wInp = ctx.getInput?.(tr, wKey);

    const w0 =
      (wInp && wCol && wCol.type === "number")
        ? readCanonFromInput(wInp, wCol)
        : num(wInp ? wInp.value : "", NaN);
    if (!Number.isFinite(w0) || w0 === 0) return { v: NaN, w: NaN };

    const w = t.absW ? Math.abs(w0) : w0;
    if (!Number.isFinite(w) || w === 0) return { v: NaN, w: NaN };

    const col = colByKey?.get?.(key);
    let v0 = NaN;
    const inp = ctx.getInput?.(tr, key);
    if (inp) {
      v0 = (col && col.type === "number") ? readCanonFromInput(inp, col) : num(inp.value, NaN);
    } else {
      const outEl = ctx.getOut?.(tr, key);
      if (outEl && col && ctx.isNumericCol?.(col)) v0 = readCanonFromOut(outEl, col);
      else if (outEl) v0 = num(outEl.textContent, NaN);
    }

    if (!Number.isFinite(v0)) return { v: NaN, w: NaN };
    const v = t.absV ? Math.abs(v0) : v0;
    return { v, w };
  }

  function computeRowContrib(tr, totalsSpec, colByKey) {
    const out = {};
    for (const t of totalsSpec) {
      const m = { sum: 0, w: 0, s: 0, seen: 0 };
      if (!tr || !t) {
        out[t?.key || ""] = m;
        continue;
      }

      if ((t.kind || "wavg") === "sum") {
        const v0 = readSumValue(tr, t.key, colByKey);
        if (Number.isFinite(v0)) {
          m.sum = t.abs ? Math.abs(v0) : v0;
          m.seen = 1;
        }
        out[t.key] = m;
        continue;
      }

      const { v, w } = readWavgParts(tr, t, colByKey);
      if (Number.isFinite(v) && Number.isFinite(w) && w !== 0) {
        m.w = w;
        m.s = w * v;
        m.seen = 1;
      }
      out[t.key] = m;
    }
    return out;
  }

  function applyContrib(acc, rowContrib, mul = 1) {
    if (!rowContrib || !acc) return;
    for (const [key, m] of Object.entries(rowContrib)) {
      const a = acc[key];
      if (!a || !m) continue;
      a.sum += (m.sum || 0) * mul;
      a.w += (m.w || 0) * mul;
      a.s += (m.s || 0) * mul;
      a.seen += (m.seen || 0) * mul;
    }
  }

  function fullRecompute(rows, totalsSpec, colByKey) {
    const acc = makeAcc(totalsSpec);
    const rowContribs = new WeakMap();

    for (const tr of rows) {
      if (!tr) continue;
      const rc = computeRowContrib(tr, totalsSpec, colByKey);
      rowContribs.set(tr, rc);
      applyContrib(acc, rc, +1);
    }

    totalsCache.acc = acc;
    totalsCache.rowContribs = rowContribs;
    totalsCache.rowCount = rows.length;
    totalsCache.firstRow = rows.length ? rows[0] : null;
    totalsCache.lastRow = rows.length ? rows[rows.length - 1] : null;
    totalsCache.sig = totalsSpecSig(totalsSpec);

    totalsDirty.all = false;
    totalsDirty.rows.clear();

    return acc;
  }

  function maybeIncremental(rows, totalsSpec, colByKey) {
    const sig = totalsSpecSig(totalsSpec);
    const dirtyCount = totalsDirty.rows.size;

    const canUseCache =
      !totalsDirty.all &&
      totalsCache.acc &&
      totalsCache.rowContribs &&
      totalsCache.sig === sig &&
      totalsCache.rowCount === rows.length &&
      totalsCache.firstRow === (rows.length ? rows[0] : null) &&
      totalsCache.lastRow === (rows.length ? rows[rows.length - 1] : null);

    if (!canUseCache) return fullRecompute(rows, totalsSpec, colByKey);

    if (dirtyCount === 0) return totalsCache.acc;

    const maxIncremental = Math.max(32, Math.floor(rows.length * 0.5));
    if (dirtyCount > maxIncremental) return fullRecompute(rows, totalsSpec, colByKey);

    for (const r of totalsDirty.rows) {
      const tr = rows[r];
      if (!tr) continue;

      const prev = totalsCache.rowContribs.get(tr);
      if (prev) applyContrib(totalsCache.acc, prev, -1);

      const next = computeRowContrib(tr, totalsSpec, colByKey);
      totalsCache.rowContribs.set(tr, next);
      applyContrib(totalsCache.acc, next, +1);
    }

    totalsDirty.rows.clear();
    totalsDirty.all = false;
    return totalsCache.acc;
  }

  function markDirtyFromEventTarget(target) {
    const tr = target?.closest?.("tr");
    if (!tr || tr.dataset?.teVirtualSpacer === "1") return;
    const r = parseInt(tr.dataset?.teRowIdx || "-1", 10);
    if (Number.isInteger(r) && r >= 0) markTotalsDirtyRow(r);
    else markTotalsDirtyAll();
  }

  if (ctx.table) {
    const on =
      typeof ctx.on === "function"
        ? ctx.on
        : (target, type, handler, options) => {
            target?.addEventListener?.(type, handler, options);
            return handler;
          };
    on(ctx.table, "input", (e) => markDirtyFromEventTarget(e?.target), true);
    on(ctx.table, "change", (e) => markDirtyFromEventTarget(e?.target), true);
  }

  function updateTotals() {
    const spec = ctx.spec;
    const table = ctx.table;
    const cols = ctx.cols || [];
    const colByKey = ctx.colByKey;
    const hiddenCols = ctx.hiddenCols;

    if (!spec?.totals || !Array.isArray(spec.totals) || spec.totals.length === 0) return;
    if (!table) return;

    const tfoot = table.querySelector("tfoot") || ensureSection(table, "tfoot");
    try { tfoot.setAttribute("role", "rowgroup"); } catch {}

    // Ensure TOTAL row exists
    let trTotal = tfoot.querySelector("tr.te-total-row");
    if (!trTotal) {
      trTotal = document.createElement("tr");
      trTotal.className = "te-total-row";
      tfoot.appendChild(trTotal);
    }

    // Ensure correct cell count & hide state
    if (trTotal.children.length !== cols.length) {
      trTotal.innerHTML = "";
      cols.forEach((c, idx) => {
        const td = document.createElement("td");
        td.dataset.colKey = c.key;
        td.dataset.colIdx = String(idx);

        const cellAlign = c.cellAlign ?? c.align;
        if (cellAlign) td.style.textAlign = String(cellAlign);

        if (hiddenCols?.has?.(c.key)) td.style.display = "none";
        trTotal.appendChild(td);
      });
    } else {
      [...trTotal.children].forEach((td, idx) => {
        const c = cols[idx];
        if (!c) return;
        td.style.display = hiddenCols?.has?.(c.key) ? "none" : "";
        const cellAlign = c.cellAlign ?? c.align;
        if (cellAlign) td.style.textAlign = String(cellAlign);
      });
    }

    // Label cell
    const labelIdx = cols.findIndex(
      (c) => c && !hiddenCols?.has?.(c.key) && c.kind !== "rowHandle" && c.kind !== "actions"
    );
    if (labelIdx >= 0) {
      const td = trTotal.children[labelIdx];
      if (td) td.textContent = "TOTAL";
    }

    const totalsSpec = normalizeTotalsSpec(spec.totals);
    if (!totalsSpec.length) return;

    const rows = ctx.getRowEls?.() || [];
    const acc = maybeIncremental(rows, totalsSpec, colByKey);

    // clear numeric cells (keep TOTAL label)
    cols.forEach((c, idx) => {
      if (!c || hiddenCols?.has?.(c.key)) return;
      if (idx === labelIdx) return;
      const td = trTotal.children[idx];
      if (td) td.textContent = "";
    });

    totalsSpec.forEach((t) => {
      const a = acc[t.key];
      if (!a) return;

      // blank if no finite values existed
      if (a.seen === 0) {
        if (t.elId) {
          const el = document.getElementById(t.elId);
          if (el) el.textContent = "";
        }
        return;
      }

      const v = (t.kind === "sum") ? a.sum : (a.w !== 0 ? (a.s / a.w) : 0);
      const col = colByKey?.get?.(t.key) || { key: t.key, type: "number", kind: "output", format: { dp: 6 } };
      const txt = colFormat(col, v);

      // footer cell
      const idx = ctx.colIndexByKey?.(t.key) ?? -1;
      if (idx >= 0) {
        const td = trTotal.children[idx];
        if (td) td.textContent = txt;
      }

      // external target
      if (t.elId) {
        const el = document.getElementById(t.elId);
        if (el) el.textContent = txt;
      }
    });

    // hide totals row when no body rows
    trTotal.style.display = rows.length ? "table-row" : "none";

    // ensure autosize includes totals row content
    try {
      const keysToFit = [...new Set(totalsSpec.map((t) => t.key).filter(Boolean))];
      if (keysToFit.length && ctx.autosizeColumns) ctx.autosizeColumns(keysToFit);
    } catch {}
    try { ctx.refreshGridAriaCounts?.(); } catch {}
  }

  ctx.markTotalsDirtyAll = markTotalsDirtyAll;
  ctx.markTotalsDirtyRow = markTotalsDirtyRow;
  ctx.markTotalsDirtyRows = markTotalsDirtyRows;
  ctx.markTotalsRowsInserted = markTotalsRowsInserted;
  ctx.markTotalsRowsRemoved = markTotalsRowsRemoved;
  ctx.updateTotals = updateTotals;
}
